import { RefreshOutlined } from '@mui/icons-material';
import { useState } from 'react';
import styles from '@shared/styles/components/General.module.css';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { isMobile, isMobileOnly } from 'react-device-detect';
import { SystemIcon } from '@shared/designSystem/SystemIcon';
import { Text } from '@shared/designSystem/Texts';
import type { ComponentProps } from 'react';
import { HorizontalContainer } from './Containers';
import { TooltipWrapper } from './TooltipWrapper';

interface ButtonProps {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    hollowed?: boolean;
    backgroundColor?: string;
    textColor?: string;
    borderColor?: string;
    width?: string;
    height?: string;
    children?: React.ReactNode;
    className?: string;
}

const Button: React.FC<ButtonProps> = ({ 
		text, 
		onClick,
		disabled = false,
		loading = false,
		backgroundColor, 
		textColor,
        borderColor,
        width = designSystemStyles.sizeButtonWidth,
        height = designSystemStyles.sizeButtonHeight,
        children,
        className
	}) => {

    const customStyle = {
		backgroundColor: backgroundColor,
		color: textColor,
		borderColor: borderColor,
        height: height,
        width: width
	};

	const disabledStyle = {
		backgroundColor: 'transparent',
		color: designSystemStyles.colorDisabledBackground,
		border: '1px solid '+designSystemStyles.colorDisabledBackground,
		cursor: "not-allowed",
        height: height,
        width: width
	};

	// When loading, don't apply the disabledStyle
	const appliedStyle = disabled && !loading ? disabledStyle : customStyle;
    const buttonClassName = [
        styles.button,
        loading ? styles['loading-button'] : '',
        disabled && !loading ? styles['disabled-button'] : '',
        'DSButton',
        className
    ].filter(Boolean).join(' ');

	return (
		<div 
			className={buttonClassName}
			onClick={disabled || loading ? () => {} : onClick} 
			onKeyDown={(event) => {
				if (disabled || loading) return
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault()
					onClick()
				}
			}}
			style={appliedStyle}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            aria-busy={loading}
		>
			{loading && <span className={styles.skeleton}></span>}
			<span className={styles['button-text']}>{text}</span>
            {children && <span className={styles['button-content']}>{children}</span>}
		</div>
	);
};


interface SpecializedButtonProps extends Omit<ButtonProps, 'backgroundColor' | 'textColor' | 'borderColor'> {}
interface RefreshButtonProps extends Omit<SpecializedButtonProps, 'text'> {
    text?: string;
}
interface IconButtonProps extends Omit<ButtonProps, 'backgroundColor' | 'textColor' | 'borderColor' | 'text'> {}
interface IconButtonBaseProps extends IconButtonProps {
    iconType: ComponentProps<typeof SystemIcon>["type"];
    iconColor?: string;
    tooltip?: React.ReactNode;
    tooltipPlacement?: ComponentProps<typeof TooltipWrapper>["placement"];
}
interface GoogleButtonProps extends Omit<ButtonProps, 'backgroundColor' | 'textColor' | 'borderColor'> {}
interface CheckButtonProps {
    text: string;
    selected: boolean;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
}
interface DateTriggerButtonProps extends Omit<ButtonProps, 'backgroundColor' | 'textColor' | 'borderColor'> {
    placeholder?: string;
}

export const HollowedButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            className={['DSHollowedButton', props.className].filter(Boolean).join(' ')}
            backgroundColor='transparent'
            textColor='var(--color-main-action)'
            borderColor='var(--color-main-action)'
        />
    );
};


export const NormalButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            className={['DSNormalButton', props.className].filter(Boolean).join(' ')}
            backgroundColor='var(--color-main-action)'
            textColor='white'
            borderColor='var(--color-main-action)'
        />
    );
};


export const TintedButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            className={['DSTintedButton', props.className].filter(Boolean).join(' ')}
            backgroundColor='#ec6c561a'
            textColor='var(--color-main-action)'
            borderColor='#ec6c561a'
        />
    );
};

export const GrayButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            className={['DSGrayButton', props.className].filter(Boolean).join(' ')}
            backgroundColor='var(--color-dropdown-background)'
            textColor='var(--color-dropdown-text)'
            borderColor='var(--color-dropdown-background)'
        />
    );
};

const GoogleIcon: React.FC = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none" style={{ marginRight: '16px' }}>
            <path d="M26.7374 11.356H25.65V11.3H13.5V16.7H21.1295C20.0165 19.8435 17.0255 22.1 13.5 22.1C9.02678 22.1 5.4 18.4732 5.4 14C5.4 9.52678 9.02678 5.9 13.5 5.9C15.5648 5.9 17.4433 6.67895 18.8737 7.95132L22.6922 4.13285C20.281 1.88577 17.0559 0.5 13.5 0.5C6.04463 0.5 0 6.54463 0 14C0 21.4554 6.04463 27.5 13.5 27.5C20.9554 27.5 27 21.4554 27 14C27 13.0948 26.9069 12.2113 26.7374 11.356Z" fill="#FFC107"/>
            <path d="M1.55664 7.71642L5.99207 10.9692C7.19222 7.9979 10.0988 5.9 13.5001 5.9C15.5649 5.9 17.4434 6.67895 18.8738 7.95132L22.6922 4.13285C20.2811 1.88577 17.056 0.5 13.5001 0.5C8.31474 0.5 3.81789 3.42747 1.55664 7.71642Z" fill="#FF3D00"/>
            <path d="M13.4998 27.5C16.9869 27.5 20.1553 26.1655 22.5509 23.9954L18.3726 20.4597C16.9717 21.5252 15.2599 22.1014 13.4998 22.1C9.98847 22.1 7.007 19.861 5.8838 16.7365L1.48145 20.1283C3.7157 24.5003 8.25305 27.5 13.4998 27.5Z" fill="#4CAF50"/>
            <path d="M26.7374 11.3561H25.65V11.3H13.5V16.7H21.1295C20.5971 18.1961 19.638 19.5034 18.3708 20.4605L18.3728 20.4591L22.5511 23.9948C22.2554 24.2634 27 20.75 27 14C27 13.0949 26.9068 12.2113 26.7374 11.3561Z" fill="#1976D2"/>
        </svg>
    );
};

export const GoogleButton: React.FC<GoogleButtonProps> = ({ text, ...props }) => {
    return (
        <Button
            {...props}
            text=""
            className={['DSGoogleButton', props.className].filter(Boolean).join(' ')}
            backgroundColor='white'
            textColor='black'
            borderColor='#DDDFE3'
        >
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                fontFamily: 'Inter, sans-serif',
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: 'normal'
            }}>
                <GoogleIcon />
                <span>{text}</span>
            </div>
        </Button>
    );
};

export const CheckButton: React.FC<CheckButtonProps> = ({
    text,
    selected,
    onClick,
    disabled = false,
    className
}) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
            return;
        }

        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <div
            className={['DSCheckButton', className].filter(Boolean).join(' ')}
            role="checkbox"
            aria-checked={selected}
            tabIndex={disabled ? -1 : 0}
            onClick={disabled ? () => {} : onClick}
            onKeyDown={handleKeyDown}
            style={{
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: designSystemStyles.gapSm,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
                userSelect: 'none'
            }}
        >
            <div
                style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '4px',
                    border: `1px solid ${selected ? designSystemStyles.colorMainAction : designSystemStyles.colorTextInputBorder}`,
                    backgroundColor: selected ? designSystemStyles.colorMainAction : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: '0.15s ease'
                }}
            >
                {selected && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M9 1L3.5 6.5L1 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
            <Text text={text} color={designSystemStyles.colorText} />
        </div>
    );
};

export const GhostButton: React.FC<SpecializedButtonProps> = (props) => {
    return (
        <Button
            {...props}
            className={['DSGhostButton', props.className].filter(Boolean).join(' ')}
            backgroundColor='transparent'
            textColor='var(--color-main-action)'
            borderColor='var(--color-dropdown-background)'
        />
    );
};


export const RefreshButton: React.FC<RefreshButtonProps> = (props) => {
    const [isRotating, setIsRotating] = useState(false);

    const handleClick = () => {
        setIsRotating(true);
        props.onClick?.();
        setTimeout(() => setIsRotating(false), 500);
    };

    return (
        <Button
            {...props}
            className={['DSRefreshButton', props.className].filter(Boolean).join(' ')}
            text={props.text ?? ''}
            textColor='var(--color-main-action)'
            borderColor='transparent'
            onClick={handleClick}
            width={isMobileOnly ? designSystemStyles.sizeIconWidth : designSystemStyles.sizeButtonHeight}
        >
            <div className={`${styles['refresh-button']} ${isRotating ? styles.rotate : ''}`}>
                <RefreshOutlined style={{ 
                    color: designSystemStyles.colorMainAction, 
                    fontSize: isMobileOnly ? '24' : '32'
                }} />
            </div>
        </Button>
    );
};

export const DateTriggerButton: React.FC<DateTriggerButtonProps> = ({
    text,
    placeholder = "Seleccionar fecha".localized(),
    ...props
}) => {
    return (
        <Button
            {...props}
            className={['DSDateTriggerButton', props.className].filter(Boolean).join(' ')}
            text=''
            backgroundColor={designSystemStyles.colorBackgroundWhite}
            textColor='var(--color-dropdown-text)'
            borderColor='var(--color-text-input-border)'
            width={props.width ?? "200px"}
        >
            <HorizontalContainer
                width="100%"
                justifyContent="space-between"
                alignment="center"
                gap={designSystemStyles.gapSm}
                padding={designSystemStyles.paddingZero}
            >
                <HorizontalContainer width="auto" gap={designSystemStyles.gapSm} padding={designSystemStyles.paddingZero}>
                    <SystemIcon type="calendar" color={designSystemStyles.colorText} />
                    <Text
                        text={text || placeholder}
                        color={text ? designSystemStyles.colorText : designSystemStyles.colorTextSecondary}
                        truncate={true}
                    />
                </HorizontalContainer>
            </HorizontalContainer>
        </Button>
    );
};

export const CloseButton: React.FC<IconButtonProps> = (props) => {
    return (
        <IconButton
            {...props}
            className={['DSCloseButton', props.className].filter(Boolean).join(' ')}
            iconType="close"
        />
    );
}

export const IconButton: React.FC<IconButtonBaseProps> = ({
    iconType,
    iconColor,
    tooltip,
    tooltipPlacement = "top",
    ...props
}) => {
    const handleClick = () => {
        props.onClick?.();
    };

    const button = (
        <Button
            {...props}
            className={['DSIconButton', props.className].filter(Boolean).join(' ')}
            text=''
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderColor={'transparent'}
            onClick={handleClick}
            width="44px"
            height="44px"
        >
            <div>
                <SystemIcon type={iconType} color={iconColor ?? designSystemStyles.colorText} />
            </div>
        </Button>
    );

    if (!tooltip) {
        return button;
    }

    return (
        <TooltipWrapper content={tooltip} placement={tooltipPlacement} disabled={props.disabled}>
            {button}
        </TooltipWrapper>
    );
}

// export const CardButton: React.FC<SpecializedButtonProps> = (props) => {
//     return (
//         <ClickableWrapper onClick={() => {}}>
//             <SimpleCardContainer width="400px">
//                 <HorizontalContainer width="100%" justifyContent="space-between">
//                     <HorizontalContainer width="auto" gap={designSystemStyles.gapSm}>
//                         <SystemIcon type="people2" color={iconColor}/>
//                         <Text text={"Ver accesos".localized()} truncate={true}/>
//                     </HorizontalContainer>
//                     <HorizontalContainer width="auto" justifyContent="right">
//                         <SystemIcon type="chevron" color={iconColor}/>
//                     </HorizontalContainer>
//                 </HorizontalContainer>
//             </SimpleCardContainer>
//         </ClickableWrapper>    
//     )
// }

export default Button;
